import type { Observation } from '@medplum/fhirtypes';
import { isResultCodeSystem } from './taskUtils';

/**
 * Sets of LOINC codes that name the same clinical measurement in the same units, so readings coded
 * any of them belong on one care-plan Result row.
 *
 * A concept is **symmetric**: there is no canonical code with aliases hanging off it, because which
 * code a reading carries says nothing about which one the plan happens to track. The join rule
 * everywhere is *exact preferred code first, then concept sibling* — a reading coded exactly what the
 * plan expects keys to that, and only a reading with no expected code falls through to its concept.
 *
 * Membership is decided by **unit compatibility**: two codes are siblings only if a chart can plot
 * their values on one axis without converting. That excludes `14743-9` (glucometer glucose in mmol/L)
 * and `59261-8` (HbA1c in mmol/mol) even though both name a measurement already here. The LOINC Groups
 * file is the check that siblings share a property and a unit; it does not decide membership —
 * fasting and random glucose sharing a row is a product decision, not a terminology one.
 */
export const RESULT_CONCEPTS: readonly (readonly string[])[] = [
  ['85354-9', '55284-4'], // Blood pressure panel — Epic exports list 55284-4 first
  ['29463-7', '3141-9', '8350-1', '8351-9'], // Body weight (kg)
  ['8302-2', '3137-7', '8306-3', '8308-9'], // Body height (cm)
  ['8310-5', '8331-1', '8332-9', '76011-6'], // Body temperature (°C)
  ['1558-6', '2345-7', '2339-0', '41653-7'], // Glucose (mg/dL) — fasting and random share one Result
  ['4548-4', '17856-6', '4549-2'], // HbA1c (%)
  ['13457-7', '18262-6', '2089-1'], // LDL cholesterol (mg/dL)
  ['33914-3', '62238-1', '98979-8', '48642-3', '48643-1'], // eGFR (mL/min/1.73m²)
  ['2160-0', '38483-4'], // Creatinine (mg/dL)
];

const CONCEPT_BY_CODE: ReadonlyMap<string, readonly string[]> = new Map(
  RESULT_CONCEPTS.flatMap((concept) => concept.map((code) => [code, concept] as const)),
);

/**
 * Every code that names the same measurement as `code`, with `code` itself first. A code in no
 * concept is its own concept, so callers can expand unconditionally.
 */
export function getResultConceptCodes(code: string): readonly string[] {
  const concept = CONCEPT_BY_CODE.get(code);
  if (!concept) return [code];
  return [code, ...concept.filter((sibling) => sibling !== code)];
}

/** Whether two codes name the same measurement — the same code, or siblings in one concept. */
export function isSameResultConcept(a: string, b: string): boolean {
  if (a === b) return true;
  return CONCEPT_BY_CODE.get(a) === CONCEPT_BY_CODE.get(b) && CONCEPT_BY_CODE.has(a);
}

/**
 * The code an Observation should be filed under, given the codes a view expects (a care plan's
 * measurement templates, say). An exact match wins — an Epic-shaped blood pressure carries both
 * `55284-4` and `85354-9`, and the plan's own `85354-9` is the right row — and only then does a
 * sibling of an expected code count. Returns undefined when the reading carries nothing the view
 * expects, so callers can fall back to their own keying.
 */
export function resolveObservationResultCode(
  observation: Observation,
  preferred: ReadonlySet<string>,
): string | undefined {
  if (!preferred.size) return undefined;
  const codes = (observation.code?.coding ?? []).flatMap((coding) =>
    isResultCodeSystem(coding.system) && coding.code ? [coding.code] : [],
  );

  const exact = codes.find((code) => preferred.has(code));
  if (exact) return exact;

  for (const code of codes) {
    const sibling = getResultConceptCodes(code).find((candidate) => preferred.has(candidate));
    if (sibling) return sibling;
  }
  return undefined;
}

/**
 * The codes to fetch for each expected code: itself plus its concept siblings, minus any sibling that
 * is *itself* expected. Two Results tracking two codes of one concept — a plan that lists both fasting
 * and random glucose as separate measurements — each keep their own reading rather than both querying
 * for both and double-counting.
 */
export function expandExpectedResultCodes(expected: readonly string[]): Map<string, string[]> {
  const expectedSet = new Set(expected);
  return new Map(
    expected.map((code) => [code, getResultConceptCodes(code).filter((c) => c === code || !expectedSet.has(c))]),
  );
}

/**
 * LOINC codes for the vitals whose metric units are translated to imperial: body weight, body height
 * and body temperature, including the variant codes external sources send. These are recognition
 * codes for ingested data — distinct from the canonical set Liza writes (`VITAL_SIGN_CONFIGS` in
 * `@liza/medplum-config`), which only the primary three overlap.
 */
export const CONVERTIBLE_VITAL_LOINC_CODES: ReadonlySet<string> = new Set(
  [...getResultConceptCodes('29463-7'), ...getResultConceptCodes('8302-2'), ...getResultConceptCodes('8310-5')].sort(),
);
